import React, { useCallback, useMemo } from 'react';
import {
  StyleSheet,
  TextInput,
  Pressable,
  View,
  ViewStyle,
  TextStyle,
} from 'react-native';
import Feather from '@react-native-vector-icons/feather/static';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Typography from './Typography';
import { fs, iconSize, s, borderWidth, v } from '../../utils/libs';
import { useTheme, GTFonts, useLayoutSize } from '../../theme';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';

export interface AppSearchBarWebProps {
  // Core
  placeholder: string;

  // Controlled mode
  value?: string;
  onChangeText?: (text: string) => void;
  onClear?: () => void;
  onSubmit?: () => void;

  // Features
  showLeadingIcon?: boolean;
  showBarcodeScan?: boolean;
  showClear?: boolean;
  onBarcodeScan?: () => void;

  // Styling
  width?: number | string;
  height?: number;
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;

  // Refs
  inputRef?: React.RefObject<TextInput | null>;

  // Additional props
  autoFocus?: boolean;
  returnKeyType?: 'search' | 'done' | 'go' | 'next' | 'send';
}

const AppSearchBarWeb: React.FC<AppSearchBarWebProps> = ({
  placeholder,
  value = '',
  onChangeText,
  onClear,
  onSubmit,
  showLeadingIcon = true,
  showBarcodeScan = false,
  showClear = true,
  onBarcodeScan,
  width,
  height,
  containerStyle,
  inputStyle,
  inputRef,
  autoFocus = false,
  returnKeyType = 'search',
}) => {
  const { theme } = useTheme();
  const { width: deviceWidth, height: deviceHeight } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    deviceWidth,
    deviceHeight,
  );
  const styles = useMemo(
    () => createStyles(theme, showTabletSidebarLayoutSetting, width, height),
    [theme, width, height, showTabletSidebarLayoutSetting],
  );

  // Handle text change
  const handleChangeText = useCallback(
    (text: string) => {
      if (onChangeText) {
        onChangeText(text);
      }
    },
    [onChangeText],
  );

  // Handle clear
  const handleClear = useCallback(() => {
    if (onClear) {
      onClear();
    } else if (onChangeText) {
      onChangeText('');
    }
  }, [onClear, onChangeText]);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit();
    }
  }, [onSubmit]);

  const hasText = value.length > 0;

  return (
    <View style={[styles.container, containerStyle]}>
      {showLeadingIcon && (
        <Feather
          name="search"
          size={iconSize(20)}
          color={theme.colors.textGray600}
          style={styles.leadingIcon}
        />
      )}
      <TextInput
        ref={inputRef as React.RefObject<TextInput>}
        style={[styles.input, inputStyle]}
        placeholder={placeholder}
        placeholderTextColor={theme.colors.textGray500}
        value={value}
        onChangeText={handleChangeText}
        onSubmitEditing={handleSubmit}
        returnKeyType={returnKeyType}
        autoFocus={autoFocus}
        autoCapitalize="none"
        autoCorrect={false}
      />
      {showClear && hasText && (
        <Pressable
          onPress={handleClear}
          style={styles.clearButton}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Typography style={styles.clearButtonText}>×</Typography>
        </Pressable>
      )}
      {showBarcodeScan && (
        <Pressable onPress={onBarcodeScan} style={styles.scanButton}>
          <MaterialCommunityIcons
            name="barcode-scan"
            size={iconSize(22)}
            color={theme.colors.primary}
          />
        </Pressable>
      )}
    </View>
  );
};

export default AppSearchBarWeb;

const createStyles = (
  theme: any,
  showTabletSidebarLayoutSetting: boolean,
  width?: number | string,
  height?: number,
) => {
  const barHeight = height ?? v(showTabletSidebarLayoutSetting ? 48 : 48);
  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      backgroundColor: '#fff',
      borderRadius: theme.borderRadius?.md ?? s(16),
      paddingHorizontal: s(16),
      marginRight: s(10),
      minHeight: barHeight,
      height: barHeight,
      flex: width === undefined ? 1 : undefined,
      width: width as any,
    },
    leadingIcon: {
      flexShrink: 0,
    },
    input: {
      flex: 1,
      fontFamily: GTFonts.regular,
      fontSize: fs(15),
      lineHeight: fs(22),
      color: theme.colors.text,
      height: '100%',
      outlineStyle: 'none',
      outlineWidth: 0,
      outlineColor: 'transparent',
    } as any,
    clearButton: {
      justifyContent: 'center',
      alignItems: 'center',
      height: s(26),
      width: s(26),
      borderRadius: s(13),
      backgroundColor: '#FF634740',
      marginLeft: s(8),
    },
    clearButtonText: {
      fontSize: fs(18),
      lineHeight: fs(18),
      marginBottom: v(1),
      color: 'red',
    },
    scanButton: {
      padding: s(8),
      marginLeft: s(4),
      borderLeftWidth: borderWidth(1),
      borderLeftColor: '#E0E0E0',
    },
  });
};
